import { createPortal } from 'react-dom';
import { ExternalLink, X } from 'lucide-react';

interface GmailReauthModalProps {
  email: string | null;
  connecting: boolean;
  onRefresh: () => void;
  onDismiss: () => void;
}

/** Same shell as the Integrations Gmail connect modal, with a refresh action. */
export function GmailReauthModal({ email, connecting, onRefresh, onDismiss }: GmailReauthModalProps) {
  return createPortal(
    <>
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 100002,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: 1,
          pointerEvents: 'auto',
        }}
        onClick={onDismiss}
      />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none sm:items-center" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:h-auto sm:rounded-[20px] sm:shadow-xl sm:max-w-lg sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60 bg-white">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/Gmailicon.png" alt="" className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl shadow-sm flex-shrink-0 object-contain" draggable={false} />
              <div>
                <h2 className="text-base sm:text-xl font-semibold text-gray-900">Gmail Integration</h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Re-authentication required</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="Dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4 sm:p-5">
                <p className="text-sm text-gray-600 mb-4">
                  Your Gmail connection needs to be refreshed before email can send and arrive. Sign in again to refresh it here.
                </p>
                {email ? (
                  <div className="mb-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-900">{email}</span>
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={connecting}
                  className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {connecting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Refresh connection
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onDismiss}
                  className="mt-3 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

interface GmailReauthBannerProps {
  onRefresh: () => void;
  onClose: () => void;
}

/** Sits under the app nav and above the Messages inbox. Messages only. */
export function GmailReauthBanner({ onRefresh, onClose }: GmailReauthBannerProps) {
  return (
    <div className="flex-shrink-0 border-b border-red-200 bg-red-50">
      <div className="flex items-center gap-2 px-3 py-2 sm:px-4">
        <button
          type="button"
          onClick={onRefresh}
          className="flex min-w-0 flex-1 items-center gap-2 text-left touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <img src="/Gmailicon.png" alt="" className="h-5 w-5 flex-shrink-0 rounded object-contain" draggable={false} />
          <span className="truncate text-sm font-medium text-red-950">Refresh Gmail connection</span>
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-1.5 text-red-700 hover:bg-red-100 transition-colors touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
